import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, EnvelopeSimple, MapPin, PaperPlaneRight, LinkedinLogo, InstagramLogo, Star } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fallbackServiceTypes = [
  { value: 'commercial-waste', label: 'Commercial Waste Collection' },
  { value: 'driver-training', label: 'Driver Training Academy' },
  { value: 'event-cleaning', label: 'Event Cleansing Services' },
  { value: 'vehicle-repair', label: 'Commercial Vehicle Repair' },
  { value: 'highways', label: 'Highways Maintenance' },
  { value: 'waste-transfer', label: 'Waste Transfer & Disposal' },
  { value: 'other', label: 'Other Enquiry' },
];

export const ContactForm = ({ isFullPage = false }) => {
  const [serviceTypes, setServiceTypes] = useState(fallbackServiceTypes);
  const [driverCourses, setDriverCourses] = useState([]);
  const [details, setDetails] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    service_type: '',
    driver_training_course: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/service-types`).then(r => {
      if (r.data && r.data.length > 0) setServiceTypes(r.data);
    }).catch(() => {});
    axios.get(`${API}/page-content/contact_details`).then(r => setDetails(r.data || {})).catch(() => setDetails({}));
    axios.get(`${API}/driver-training-courses`).then(r => {
      if (r.data && r.data.length > 0) setDriverCourses(r.data);
    }).catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    // Switching away from driver-training? Clear the course selection.
    setFormData((prev) => ({
      ...prev,
      service_type: value,
      driver_training_course: value === 'driver-training' ? prev.driver_training_course : '',
    }));
  };

  const handleCourseChange = (value) => {
    setFormData((prev) => ({ ...prev, driver_training_course: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.service_type === 'driver-training' && !formData.driver_training_course) {
      toast.error('Please select a course type.');
      return;
    }
    setIsSubmitting(true);

    try {
      const payload = { ...formData };
      // Only include the course field if the user actually selected Driver Training,
      // otherwise drop it so backend never stores a stale value.
      if (payload.service_type !== 'driver-training') {
        delete payload.driver_training_course;
      }
      await axios.post(`${API}/contact`, payload);
      toast.success('Thank you for your enquiry! We will be in touch soon.');
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company: '',
        service_type: '',
        driver_training_course: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('There was an error submitting your enquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      className={`${isFullPage ? 'py-32 pt-40' : 'py-24 lg:py-32'} bg-neutral-50`}
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="accent-font text-lime-600 mb-4">Get In Touch</p>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 tracking-tight mb-6">
              Contact <span className="text-lime-600">Us</span>
            </h2>
            <p className="text-neutral-600 leading-relaxed mb-10">
              Ready to discuss your waste management needs? Contact us today for a free, 
              no-obligation quote. Our friendly team is here to help.
            </p>

            <div className="space-y-4">
              <a
                href={details?.phone_href || 'tel:07821666083'}
                className="flex items-center gap-4 p-4 rounded-xl bg-white border border-neutral-200 hover:border-lime-300 hover:shadow-md transition-all duration-200"
                data-testid="contact-phone-link"
              >
                <div className="p-3 rounded-lg bg-lime-50">
                  <Phone size={24} weight="duotone" className="text-lime-600" />
                </div>
                <div>
                  <p className="text-neutral-500 text-sm">Phone</p>
                  <p className="text-neutral-900 font-medium">{details?.phone_display || '07821 666 083'}</p>
                </div>
              </a>

              <a
                href={`mailto:${details?.email || 'enquiries@nsecltd.co.uk'}`}
                className="flex items-center gap-4 p-4 rounded-xl bg-white border border-neutral-200 hover:border-lime-300 hover:shadow-md transition-all duration-200"
                data-testid="contact-email-link"
              >
                <div className="p-3 rounded-lg bg-lime-50">
                  <EnvelopeSimple size={24} weight="duotone" className="text-lime-600" />
                </div>
                <div>
                  <p className="text-neutral-500 text-sm">Email</p>
                  <p className="text-neutral-900 font-medium">{details?.email || 'enquiries@nsecltd.co.uk'}</p>
                </div>
              </a>

              <div className="flex items-start gap-4 p-4 rounded-xl bg-white border border-neutral-200">
                <div className="p-3 rounded-lg bg-lime-50">
                  <MapPin size={24} weight="duotone" className="text-lime-600" />
                </div>
                <div>
                  <p className="text-neutral-500 text-sm">Address</p>
                  <p className="text-neutral-900 font-medium" data-testid="contact-address">
                    {details?.address_line_1 || 'Unit 5-6 Westlands Distribution Park'}<br />
                    {details?.address_line_2 || 'Winterstoke Road'}<br />
                    {details?.address_line_3 || 'Weston-Super-Mare, BS24 9AD'}
                  </p>
                </div>
              </div>

              {/* Social Media Links */}
              <div className="flex items-center gap-4 pt-4">
                <a href={details?.linkedin_url || 'https://www.linkedin.com/company/north-somerset-environment'} target="_blank" rel="noopener noreferrer" className="p-3 rounded-lg bg-white border border-neutral-200 hover:border-lime-300 hover:shadow-md transition-all duration-200 text-neutral-500 hover:text-lime-600" data-testid="contact-social-linkedin" aria-label="LinkedIn">
                  <LinkedinLogo size={22} weight="bold" />
                </a>
                <a href={details?.instagram_url || 'https://www.instagram.com/nsecltd'} target="_blank" rel="noopener noreferrer" className="p-3 rounded-lg bg-white border border-neutral-200 hover:border-lime-300 hover:shadow-md transition-all duration-200 text-neutral-500 hover:text-lime-600" data-testid="contact-social-instagram" aria-label="Instagram">
                  <InstagramLogo size={22} weight="bold" />
                </a>
                <a href={details?.trustpilot_url || 'https://uk.trustpilot.com/review/nsecltd.co.uk'} target="_blank" rel="noopener noreferrer" className="p-3 rounded-lg bg-white border border-neutral-200 hover:border-lime-300 hover:shadow-md transition-all duration-200 text-neutral-500 hover:text-lime-600" data-testid="contact-social-trustpilot" aria-label="Trustpilot">
                  <Star size={22} weight="bold" />
                </a>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="p-8 rounded-2xl bg-white border border-neutral-200 shadow-lg"
          >
            <h3 className="font-outfit font-semibold text-2xl text-neutral-900 mb-6">
              Send us a Message
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-neutral-700">First Name *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="bg-neutral-50 border-neutral-200 text-neutral-900 placeholder:text-neutral-400 focus:border-lime-500 focus:ring-lime-500/20"
                    placeholder="John"
                    data-testid="contact-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-neutral-700">Last Name *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="bg-neutral-50 border-neutral-200 text-neutral-900 placeholder:text-neutral-400 focus:border-lime-500 focus:ring-lime-500/20"
                    placeholder="Smith"
                    data-testid="contact-last-name"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-neutral-700">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-neutral-50 border-neutral-200 text-neutral-900 placeholder:text-neutral-400 focus:border-lime-500 focus:ring-lime-500/20"
                    placeholder="john@example.com"
                    data-testid="contact-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-neutral-700">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="bg-neutral-50 border-neutral-200 text-neutral-900 placeholder:text-neutral-400 focus:border-lime-500 focus:ring-lime-500/20"
                    placeholder="07821 666 083"
                    data-testid="contact-phone"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company" className="text-neutral-700">Company Name</Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="bg-neutral-50 border-neutral-200 text-neutral-900 placeholder:text-neutral-400 focus:border-lime-500 focus:ring-lime-500/20"
                  placeholder="Your company name"
                  data-testid="contact-company"
                />
              </div>

              <div className="space-y-2 relative z-20">
                <Label className="text-neutral-700">Service Type *</Label>
                <Select
                  value={formData.service_type}
                  onValueChange={handleServiceChange}
                  required
                >
                  <SelectTrigger 
                    className="bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-lime-500 focus:ring-lime-500/20"
                    data-testid="contact-service-select"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-neutral-200 z-[100]">
                    {serviceTypes.map((service) => (
                      <SelectItem 
                        key={service.value} 
                        value={service.value}
                        className="text-neutral-700 focus:bg-lime-50 focus:text-neutral-900 cursor-pointer"
                        data-testid={`service-option-${service.value}`}
                      >
                        {service.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.service_type === 'driver-training' && (
                <div className="space-y-2 relative z-10" data-testid="contact-course-wrapper">
                  <Label className="text-neutral-700">Course Type *</Label>
                  <Select
                    value={formData.driver_training_course}
                    onValueChange={handleCourseChange}
                    required
                  >
                    <SelectTrigger
                      className="bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-lime-500 focus:ring-lime-500/20"
                      data-testid="contact-course-select"
                    >
                      <SelectValue placeholder="Select a course" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-neutral-200 z-[100]">
                      {(driverCourses.length > 0 ? driverCourses : [{ value: 'other', label: 'Other / Not sure' }]).map((course) => (
                        <SelectItem
                          key={course.value}
                          value={course.value}
                          className="text-neutral-700 focus:bg-lime-50 focus:text-neutral-900 cursor-pointer"
                          data-testid={`course-option-${course.value}`}
                        >
                          {course.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="message" className="text-neutral-700">Message *</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="bg-neutral-50 border-neutral-200 text-neutral-900 placeholder:text-neutral-400 focus:border-lime-500 focus:ring-lime-500/20 resize-none"
                  placeholder="Tell us about your requirements..."
                  data-testid="contact-message"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-lime-600 hover:bg-lime-700 text-white font-semibold py-6 rounded-full"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    Send Message
                    <PaperPlaneRight size={20} weight="bold" className="ml-2" />
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
